import React from "react";
import { useDisclaimer } from "../context/DisclaimerContext";
import { useLanguage } from "../context/LanguageContext";
import { ShieldAlert, BookOpen } from "lucide-react";

const DisclaimerModal = () => {
  const { accepted, accept, hydrated } = useDisclaimer();
  const { t } = useLanguage();

  if (!hydrated || accepted) return null;

  return (
    <div
      data-testid="disclaimer-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
      style={{
        background: "rgba(44, 85, 69, 0.35)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        data-testid="disclaimer-modal"
        className="glass w-full max-w-2xl rounded-3xl p-8 sm:p-12 animate-fade-in-up relative overflow-hidden"
        role="dialog"
        aria-modal="true"
      >
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-islamic-pattern opacity-50 pointer-events-none" />

        <div className="flex items-start gap-4 mb-6 relative">
          <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-clay/10 text-clay">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2 font-medium">
              {t("disclaimer_label")}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-forest font-semibold leading-tight">
              {t("disclaimer_title")}
            </h2>
          </div>
        </div>

        <div className="space-y-4 text-mute leading-relaxed text-sm sm:text-base">
          <p>
            <span className="text-forest font-medium">{t("disclaimer_intro_1")}</span>{" "}
            {t("disclaimer_intro_2")} <em>{t("disclaimer_intro_3")}</em>.
          </p>
          <ul className="space-y-2 pl-4">
            <li className="flex gap-3">
              <span className="text-gold mt-1">◆</span>
              <span>
                {t("disclaimer_point_1_a")}{" "}
                <strong className="text-ink">{t("disclaimer_point_1_b")}</strong>{" "}
                {t("disclaimer_point_1_c")}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-gold mt-1">◆</span>
              <span>
                {t("disclaimer_point_2_a")}{" "}
                <strong className="text-ink">{t("disclaimer_point_2_b")}</strong>{" "}
                {t("disclaimer_point_2_c")}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-gold mt-1">◆</span>
              <span>
                {t("disclaimer_point_3_a")}{" "}
                <strong className="text-ink">{t("disclaimer_point_3_b")}</strong>
                {t("disclaimer_point_3_c")}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-gold mt-1">◆</span>
              <span>{t("disclaimer_point_4")}</span>
            </li>
          </ul>
        </div>

        <div className="ornament-divider my-8">
          <BookOpen className="w-4 h-4" />
        </div>

        <button
          data-testid="disclaimer-accept-button"
          onClick={accept}
          className="w-full bg-forest text-sand hover:bg-sage rounded-full py-4 px-8 text-sm sm:text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-forest/20"
        >
          {t("disclaimer_accept")}
        </button>

        <p className="text-center mt-4 text-xs text-mute">
          {t("disclaimer_footnote")}
        </p>
      </div>
    </div>
  );
};

export default DisclaimerModal;
